/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Const } from "const";
import { TimeoutError } from "exception/TimeoutError";
import { useEffect, useRef, useCallback } from "react";
import { getElapsedTime } from "utils/DateTime";
import { useApiWithTimeout } from "./ApiWrapperWithTimeout";
import { TasksApi } from "client";

/**
 * Return function of polling check for task status.
 * @return {Function} polling function. resolve jobSetStatus
 */
export function useWaitTaskStatus(): (
  jobSetId: string,
  completeStatus: number[],
  timeout: number
) => Promise<number> {
  const timerIdRef = useRef<NodeJS.Timeout | string | number | undefined>();
  const pollingStartTime = useRef(new Date());
  const [tasksApi, cancelTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTaskCreationProgress
  );
  useEffect(() => {
    return () => {
      // clear when unmounted.
      clearInterval(timerIdRef.current);
      cancelTasksApi();
    };
  }, [cancelTasksApi]);

  return useCallback(
    (
      jobSetId: string,
      completeStatuses: number[],
      timeout: number
    ): Promise<number> => {
      pollingStartTime.current = new Date();
      return new Promise<number>((resolve, reject) => {
        let loading = false;
        const func = async () => {
          if (loading) {
            return true;
          }
          loading = true;
          try {
            const res = await tasksApi.getTaskCreateProgress({
              jobSetId: jobSetId,
            });
            if (completeStatuses.includes(res.jobSetStatus)) {
              clearInterval(timerIdRef.current);
              resolve(res.jobSetStatus);
              return false;
            }
            if (getElapsedTime(pollingStartTime.current) > timeout) {
              throw new TimeoutError("Polling taskStatus check has timed out");
            }
          } catch (err) {
            clearInterval(timerIdRef.current);
            reject(err);
            return false;
          } finally {
            loading = false;
          }
          return true;
        };
        func().then((next) => {
          if (next) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = setInterval(func, Const.taskProgressInterval);
          }
        });
      });
    },
    [tasksApi]
  );
}
